import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { TCreateNewAdminReduxParams } from '~types/feat/admin';
import combinedActions from '~redux/combinedActions';

import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';
import config from '~config';

import * as FormStyles from './_adminsCreateAdminForm.module.scss';
import { BranchStatusEnum } from '~utils/enums';

type TAdminsCreateAdminForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewAdminReduxParams) => void;
    error: string;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
}

const AdminsCreateAdminForm: FC<TAdminsCreateAdminForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
}) => {
    const dispatch = useAppDispatch();

    const { Misc: { blockEnterKey } } = UtilFunctions;

    const allRoles = useAppSelector(state => state.admin.allRoles);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const getEmailDomain = () => {
        const baseDomain = config.emailDomain as string;

        return baseDomain;
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            formik.setFieldValue('branch', branchesList.find((branch) => branch.branchCode === branchCode)?.branchName);
        }
    }, [branchesList]);

    useEffect(() => {
        dispatch(combinedActions.adminGetAllRolesAttempt());
    }, []);

    const AdminsCreateAdminFormValidation = Yup.object().shape({
        branch: Yup.string().oneOf(branchesList.map(branch => branch.branchName), 'Invalid branch').label('Branch').required('Branch is required'),
        role: Yup.string().oneOf(allRoles.map(role => role.roleName), 'Invalid role').label('Role').required('Role is required'),
        email: Yup.string().email().matches(
            new RegExp(`^[A-Za-z0-9._%+-]+${getEmailDomain().replace('.', '\\.')}$`),
            `Email must be ${getEmailDomain()}`
        ).matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Email').required('Email is required'),
        referralCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Referral Code'),
    });

    const AdminsCreateAdminFormInitialValues = {
        branch: '',
        role: '',
        email: '',
        referralCode: '',
    };

    const formik = useFormik({
        initialValues: AdminsCreateAdminFormInitialValues,
        validationSchema: AdminsCreateAdminFormValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            const {
                role,
                branch,
                ...restOfValues
            } = values;

            const roleId = allRoles.find(item => item.roleName === role)?.roleId;
            const branchId = branchesList.find(item => item.branchName === branch)?.branchId;

            if (!roleId || !branchId) return;

            onSubmit({
                roleId,
                branchId,
                ...restOfValues,
            });
        },
    });

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map(branch => {
        return {
            label: branch.branchName,
            value: branch.branchId,
        };
    });

    const roleOptions = allRoles.map(role => {
        return {
            label: role.roleName,
            value: role.roleId,
        };
    });

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='email'
                    name='email'
                    label='Email* :'
                    value={formik.values.email}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='referralCode'
                    name='referralCode'
                    label='Referral Code :'
                    value={formik.values.referralCode}
                    error={formik.touched.referralCode ? formik.errors.referralCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='branch'
                    name='branch'
                    label='Branch* :'
                    options={branchOptions}
                    value={formik.values.branch || ''}
                    error={formik.touched.branch ? formik.errors.branch : undefined}
                    onChange={(e) => formik.setFieldValue('branch', e.target.value)}
                    autoComplete='off'
                    // onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || !allBranchAccess}
                    optionSelected={!!branchOptions.find(option => option.label === formik.values.branch)}
                />

                <LabelledDropdown
                    id='role'
                    name='role'
                    label='Role* :'
                    options={roleOptions}
                    value={formik.values.role || ''}
                    error={formik.touched.role ? formik.errors.role : undefined}
                    onChange={(e) => formik.setFieldValue('role', e.target.value)}
                    autoComplete='off'
                    // onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                    optionSelected={!!roleOptions.find(option => option.label === formik.values.role)}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default AdminsCreateAdminForm;
